/**
 * useRoutesMapIntegration Hook
 *
 * @version 1.0
 * @module core/modules/waste/hooks
 *
 * @description
 * Hook para integrar la visualización de rutas en el mapa.
 * Gestiona la inicialización de layers, actualización de datos y event listeners.
 *
 * @example
 * ```tsx
 * // En WasteProvider o MapLayout
 * import { useRoutesMapIntegration } from './hooks/useRoutesMapIntegration';
 *
 * function WasteMapProvider({ map, projectId }) {
 *   useRoutesMapIntegration({ map, projectId });
 *
 *   return <WasteModule />;
 * }
 * ```
 */

import { useEffect } from 'react';
import { mapEngine } from '../../../map-engine/MapEngineService';
import {
  initializeRoutesLayer,
  updateRoutesData,
  cleanupRoutesLayer,
  highlightRoute,
  clearSelection,
  fitBoundsToRoute,
} from '../map-layers';
import { useRoutes } from './useRoutes';

/**
 * Props del hook useRoutesMapIntegration
 */
interface UseRoutesMapIntegrationProps {
  /**
   * ID del proyecto activo
   */
  projectId: string | null;

  /**
   * ¿Visualizar rutas en el mapa?
   * @default true
   */
  enabled?: boolean;

  /**
   * ¿Cargar datos automáticamente al montar?
   * Si false, los datos se cargan solo cuando el módulo waste esté activo.
   * @default true
   */
  autoLoad?: boolean;
}

/**
 * Hook para integrar rutas con el mapa
 *
 * @remarks
 * Este hook:
 * 1. Inicializa los layers de rutas cuando el mapa está listo
 * 2. Actualiza los datos cuando routes cambian
 * 3. Escucha eventos para selección y fitBounds
 * 4. Limpia los layers al desmontar
 *
 * @param props - Configuración del hook
 */
export function useRoutesMapIntegration({
  projectId,
  enabled = true,
  autoLoad = true,
}: UseRoutesMapIntegrationProps): void {
  const { routes, loadRoutes } = useRoutes({
    projectId: projectId || '',
    autoLoad: autoLoad && enabled && !!projectId,
  });

  /**
   * Inicializar layers cuando el mapa está listo
   * Obtiene el mapa de forma reactiva en cada ejecución
   */
  useEffect(() => {
    const map = mapEngine.getMap();

    console.log('[useRoutesMapIntegration] 🔄 Init effect running', {
      hasMap: !!map,
      projectId,
      enabled
    });

    if (!map) {
      console.log('[useRoutesMapIntegration] ⚠️ No map available yet, will retry when routes change');
      return;
    }

    if (!projectId) {
      console.log('[useRoutesMapIntegration] ⚠️ No projectId available');
      return;
    }

    if (!enabled) {
      console.log('[useRoutesMapIntegration] ⏭️ Not enabled');
      return;
    }

    console.log('[useRoutesMapIntegration] ✅ Initializing layers for project:', projectId);

    // Esperar a que el mapa esté cargado
    if (!map.isStyleLoaded()) {
      console.log('[useRoutesMapIntegration] ⏳ Map style not loaded yet, waiting...');
      const onLoad = () => {
        console.log('[useRoutesMapIntegration] 🎯 Map style loaded, initializing now');
        initializeRoutesLayer(map, projectId);
        map.off('load', onLoad);
      };
      map.on('load', onLoad);
      return;
    }

    console.log('[useRoutesMapIntegration] 🚀 Initializing routes layer immediately');
    initializeRoutesLayer(map, projectId);

    // Cleanup al desmontar
    return () => {
      console.log('[useRoutesMapIntegration] 🧹 Cleaning up layers');
      cleanupRoutesLayer();
    };
  }, [projectId, enabled, routes.length]); // Agregar routes.length como dependencia para re-intentar

  /**
   * Actualizar datos cuando routes cambian
   */
  useEffect(() => {
    if (!enabled) {
      console.log('[useRoutesMapIntegration] ⏭️ Skipping update - not enabled');
      return;
    }

    console.log('[useRoutesMapIntegration] Updating routes data:', routes.length, 'routes');

    // Actualizar incluso si routes.length === 0 (para limpiar el mapa)
    updateRoutesData(routes);
  }, [routes, enabled]);

  /**
   * Escuchar eventos de rutas
   */
  useEffect(() => {
    if (!enabled) return;

    /**
     * Evento: route-select
     * Resaltar ruta en el mapa cuando se selecciona
     */
    const handleRouteSelect = (event: Event): void => {
      const { routeId } = (event as CustomEvent<{ routeId: string }>).detail;
      console.log('[useRoutesMapIntegration] Route selected:', routeId);
      highlightRoute(routeId);
    };

    /**
     * Evento: route-fit-bounds
     * Hacer zoom a la ruta
     */
    const handleFitBounds = (event: Event): void => {
      const { geometry } = (event as CustomEvent<{ geometry: any }>).detail;
      console.log('[useRoutesMapIntegration] Fit bounds to route');
      fitBoundsToRoute(geometry, 50);
    };

    /**
     * Eventos: route-created, route-updated, route-deleted
     * Re-cargar rutas desde el backend
     */
    const handleRouteChange = (): void => {
      console.log('[useRoutesMapIntegration] Route changed, reloading...');
      void loadRoutes();
    };

    /**
     * Evento: route-deselect
     * Quitar highlight de rutas
     */
    const handleRouteDeselect = (): void => {
      console.log('[useRoutesMapIntegration] Route deselected');
      clearSelection();
    };

    // Registrar listeners
    window.addEventListener('waste-route-select', handleRouteSelect);
    window.addEventListener('waste-route-fit-bounds', handleFitBounds);
    window.addEventListener('route-created', handleRouteChange);
    window.addEventListener('route-updated', handleRouteChange);
    window.addEventListener('route-deleted', handleRouteChange);
    window.addEventListener('waste-route-deselect', handleRouteDeselect);

    // Cleanup
    return () => {
      window.removeEventListener('waste-route-select', handleRouteSelect);
      window.removeEventListener('waste-route-fit-bounds', handleFitBounds);
      window.removeEventListener('route-created', handleRouteChange);
      window.removeEventListener('route-updated', handleRouteChange);
      window.removeEventListener('route-deleted', handleRouteChange);
      window.removeEventListener('waste-route-deselect', handleRouteDeselect);
    };
  }, [enabled, loadRoutes]);
}
